import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, CircularProgress, MenuItem, Typography, Box,
  FormControlLabel, Checkbox, InputAdornment, Switch, Divider
} from "@material-ui/core";
import API from "../../../../network/dashboardAPI";
import useAlert from "../../../../services/useAlert";
import {
  DELIVERY_TYPES, PAYMENT_METHODS, orderTypeFor, numberOrNull,
  readDistanceToKm, readWeightToGrams, readIsActive,
} from "../utils/helper";

const EMPTY_FORM = {
  label: "",
  deliveryType: "door_to_door",
  distanceToKm: "",
  amountTo: "",
  weightToGrams: "",
  deliveryFeeFixed: "",
  deliveryFeePerKm: "",
  allowedPaymentMethod: ["cash", "online"],
  // New rules go live immediately — an admin adding a band wants it priced.
  isActive: true,
};

const GRAMS_PER_KG = 1000;

export default function DeliveryRateDialog({ open, onClose, onSaved, initialData }) {
  const alert = useAlert();
  const isEdit = !!initialData?.id;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        label: initialData.label || "",
        deliveryType: initialData.deliveryType || "door_to_door",
        distanceToKm: readDistanceToKm(initialData) ?? "",
        amountTo: initialData.amountTo ?? "",
        weightToGrams: readWeightToGrams(initialData) ?? "",
        deliveryFeeFixed: initialData.deliveryFeeFixed ?? "",
        deliveryFeePerKm: initialData.deliveryFeePerKm ?? "",
        allowedPaymentMethod: Array.isArray(initialData.allowedPaymentMethod)
          ? initialData.allowedPaymentMethod
          : ["cash", "online"],
        isActive: readIsActive(initialData),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [open, initialData]);

  const validate = () => {
    const errs = {};

    // Every ceiling is required: a band with no upper bound on an axis would
    // overlap the catch-all row and make pricing depend on row order.
    [["distanceToKm", "Distance ceiling"], ["amountTo", "Amount ceiling"],
     ["weightToGrams", "Weight ceiling"]].forEach(([field, name]) => {
      if (form[field] === "") {
        errs[field] = `${name} is required`;
      } else if (!(parseFloat(form[field]) > 0)) {
        errs[field] = `${name} must be greater than 0`;
      }
    });

    // A zero fee is a real configuration — it is how a free-delivery row is
    // expressed — so only blank-on-both and negatives are rejected.
    if (form.deliveryFeeFixed === "" && form.deliveryFeePerKm === "") {
      errs.deliveryFeeFixed = "Set a fixed fee or a per-km fee (0 is allowed)";
    }
    [["deliveryFeeFixed", "Fixed fee"], ["deliveryFeePerKm", "Per-km fee"]].forEach(([field, name]) => {
      if (form[field] !== "" && !(parseFloat(form[field]) >= 0)) {
        errs[field] = `${name} must be zero or greater`;
      }
    });

    if (!form.allowedPaymentMethod.length) {
      errs.allowedPaymentMethod = "Select at least one payment method";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const distanceToKm = parseFloat(form.distanceToKm);
      const payload = {
        label: form.label,
        deliveryType: form.deliveryType,
        // Derived, not chosen — keeps the legacy type-filtered lookup in step
        // with the fulfilment mode, exactly as the seeder does.
        type: orderTypeFor(form.deliveryType),
        distanceToKm,
        amountTo: parseFloat(form.amountTo),
        weightToGrams: parseInt(form.weightToGrams, 10),
        deliveryFeeFixed: numberOrNull(form.deliveryFeeFixed, parseFloat) ?? 0,
        deliveryFeePerKm: numberOrNull(form.deliveryFeePerKm, parseFloat) ?? 0,
        allowedPaymentMethod: form.allowedPaymentMethod,
        isActive: form.isActive,
        // The legacy metres column drives the existing getDeliveryRange lookup
        // and the seeder keeps it at distance_to_km * 1000. Sent so a row added
        // here matches the seeded ones rather than being invisible to it.
        deliveryMaxRange: distanceToKm,
      };

      const response = isEdit
        ? await API.updateDeliveryRuleById({ param: initialData.id, body: payload })
        : await API.createDeliveryRule(payload);
      const res = await response.json();

      if (res.success) {
        alert.toast(isEdit ? "Delivery rule updated" : "Delivery rule created", "success");
        onSaved();
        onClose();
      } else {
        // Duplicate-cell rejections name the conflicting rule id.
        alert.error("Error", res.data || res.message || "Failed to save");
      }
    } catch (error) {
      alert.error("Error", "Failed to save delivery rule");
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const togglePaymentMethod = (value) => {
    setForm((prev) => ({
      ...prev,
      allowedPaymentMethod: prev.allowedPaymentMethod.includes(value)
        ? prev.allowedPaymentMethod.filter((m) => m !== value)
        : [...prev.allowedPaymentMethod, value],
    }));
    if (errors.allowedPaymentMethod) setErrors((prev) => ({ ...prev, allowedPaymentMethod: undefined }));
  };

  const kgHint = form.weightToGrams !== "" && parseFloat(form.weightToGrams) > 0
    ? `= ${(parseFloat(form.weightToGrams) / GRAMS_PER_KG).toLocaleString()} kg`
    : "Sheet is in kg — 20 kg is 20000";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Delivery Rule" : "Add Delivery Rule"}</DialogTitle>
      <DialogContent>
        <Box mb={1}>
          <Typography variant="body2" color="textSecondary">
            Only the upper bound of each band is stored — the lower bound is the previous
            band's ceiling. An order falls in the smallest ceiling strictly greater than
            its value, so a 5 kg order lands in the 20 kg band, not the 5 kg one.
          </Typography>
        </Box>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={7}>
            <TextField
              label="Label" fullWidth value={form.label}
              placeholder="e.g. PP medium"
              onChange={(e) => handleChange("label", e.target.value)}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              select label="Delivery Type *" fullWidth value={form.deliveryType}
              onChange={(e) => handleChange("deliveryType", e.target.value)}
              variant="outlined" size="small"
            >
              {DELIVERY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Distance up to *" fullWidth type="number" value={form.distanceToKm}
              onChange={(e) => handleChange("distanceToKm", e.target.value)}
              error={!!errors.distanceToKm} helperText={errors.distanceToKm}
              InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Order total up to *" fullWidth type="number" value={form.amountTo}
              onChange={(e) => handleChange("amountTo", e.target.value)}
              error={!!errors.amountTo} helperText={errors.amountTo}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Weight up to *" fullWidth type="number" value={form.weightToGrams}
              onChange={(e) => handleChange("weightToGrams", e.target.value)}
              error={!!errors.weightToGrams} helperText={errors.weightToGrams || kgHint}
              InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Fixed Fee" fullWidth type="number" value={form.deliveryFeeFixed}
              onChange={(e) => handleChange("deliveryFeeFixed", e.target.value)}
              error={!!errors.deliveryFeeFixed}
              helperText={errors.deliveryFeeFixed || "0 = free for this band"}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Fee per km" fullWidth type="number" value={form.deliveryFeePerKm}
              onChange={(e) => handleChange("deliveryFeePerKm", e.target.value)}
              error={!!errors.deliveryFeePerKm} helperText={errors.deliveryFeePerKm}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>Allowed payment methods *</Typography>
            {PAYMENT_METHODS.map((m) => (
              <FormControlLabel
                key={m.value}
                control={
                  <Checkbox
                    color="primary"
                    checked={form.allowedPaymentMethod.includes(m.value)}
                    onChange={() => togglePaymentMethod(m.value)}
                  />
                }
                label={m.label}
              />
            ))}
            {errors.allowedPaymentMethod && (
              <Typography variant="caption" color="error" display="block">
                {errors.allowedPaymentMethod}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Box mt={1}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={form.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                }
                label={form.isActive ? "Active" : "Inactive"}
              />
              <Typography variant="caption" color="textSecondary" display="block">
                {form.isActive
                  ? "This band is used when pricing orders."
                  : "Switched off — orders in this band fall through to another row. "
                    + "Deactivating is the reversible alternative to deleting."}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
